import { packCodeSchema, type PackCode } from '@bcl/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { extractErrorMessage } from '@/features/auth/error-message';
import {
  createProject,
  updateProject,
} from '@/features/projects/projects-api';

function readPackParam(value: string | null): PackCode | null {
  if (!value) return null;
  const parsed = packCodeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * `/projects/new` — point d'entree unique du funnel client.
 *
 * Le funnel BCL demarre TOUJOURS par le choix du pack (cf. demande user :
 * « on part toujours du PACK »). Cette page se contente donc de creer un
 * projet vide cote API puis redirige immediatement vers le wizard qui prend
 * le relais sur l'etape Pack. Si `?pack=` est fourni en query string, on
 * pre-renseigne le pack pour court-circuiter l'etape PackStep.
 */
export function NewProjectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetPack = useMemo(
    () => readPackParam(searchParams.get('pack')),
    [searchParams],
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    // Garde anti-double-mount (React StrictMode en dev) : on ne cree pas
    // deux projets a la suite si l'effect est rejoue.
    /* v8 ignore next */
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        // Valeurs par defaut : requestType + typology seront ecrasees a
        // l'etape Qualification du wizard.
        const project = await createProject({
          requestType: 'etude_raccordement',
          typology: 'maison_individuelle',
        });
        if (presetPack) {
          await updateProject(project.id, { packCode: presetPack });
        }
        navigate(`/projects/${project.id}/wizard`, { replace: true });
      } catch (err) {
        setServerError(extractErrorMessage(err));
      }
    })();
  }, [navigate, presetPack]);

  return (
    <main className="container py-12">
      <Card className="mx-auto max-w-2xl">
        <CardHeader>
          <CardTitle>Démarrer mon dossier</CardTitle>
          <CardDescription>
            Nous préparons votre dossier&nbsp;… vous allez être redirigé vers le
            choix du pack.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {serverError ? (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Création en cours…</p>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
